import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { CalculateInputTrigger } from "components/ui/CalculateInputTrigger/CalculateInputTrigger";
import { Select } from "components/ui/Select/Select";
import { QRisk3CalculatorModal } from "features/qriskScore/QRisk3CalculatorModal/QRisk3CalculatorModal";
import { onInvalidSubmit } from "utils/reportFormErrors";

import { ItemFormWrapper } from "../../../../../components/ItemFormWrapper/ItemFormWrapper";
import { SMOKING_STATUS_OPTIONS } from "../../../../../constants/formOptions";
import style from "./RiskAndAssesmentForm.module.css";

// OCF dataset (Ref 21a) uses "Ex-Vaping user" wording (CDM/PP datasets use "Ex-user").
const OCF_VAPING_STATUS_OPTIONS = [
  { label: "Current (daily or occasional)", value: "CURRENT" },
  { label: "Ex-Vaping user (gave up 6 months +)", value: "EX_USER" },
  { label: "Never", value: "NEVER" },
];

// OCF dataset V1.9 "Risk Factors" (Ref 20-26) = smoking status, vaping, weight,
// height, BMI, waist, QRisk3 ONLY. Alcohol, physical activity and all MECC
// interventions (smoking/weight/alcohol/PA) are intentionally NOT part of OCF —
// the BE OCF riskFactors DTO must drop these fields (see BE change request:
// `.claude/cdm-context/ocf-be-change-request-riskfactors.md`).
// Weight/height/BMI/waist are collected in the Review & Exam step. This step runs
// AFTER the Tests step, so the QRisk3 calculator prefills the bloods (cholesterol
// /HDL) the clinician has already entered.
const formSchema = z.object({
  smokingStatus: z.string().min(1, "Smoking status is required"),
  vapingStatus: z.string().min(1, "Vaping status is required"),
  qRisk3Score: z.string().min(1, "QRisk 3 score is required"),
});

export const RiskAndAssessmentForm = ({
  onBack,
  onContinue,
  data,
  isReview = false,
}) => {
  const [isQRisk3ModalOpen, setIsQRisk3ModalOpen] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      smokingStatus: data?.smokingStatus || "",
      vapingStatus: data?.vapingStatus || "",
      qRisk3Score: data?.qRisk3Score || "",
      tcHdlRatio: data?.tcHdlRatio || "",
      // QRisk3-only inputs with no other home on the OCF form — persisted here so
      // the clinician's calculator choices survive a modal re-open. Cholesterol,
      // BP and BMI are NOT stored here: they are prefilled (read-only) from the
      // prior steps (see the modal props below).
      qRisk3Sbps5: data?.qRisk3Sbps5 || "",
      // null (not 1) so the calculator can seed ethnicity from the patient's HSE
      // ethnic group on a new review; a stored review value still wins.
      qRisk3Ethrisk: data?.qRisk3Ethrisk ?? null,
      qRisk3Conditions: data?.qRisk3Conditions ?? {},
      qRisk3CigarettesPerDay: data?.qRisk3CigarettesPerDay || "",
    },
  });

  const handleFormSubmit = (values) => {
    onContinue(values);
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={form.handleSubmit(handleFormSubmit, onInvalidSubmit("RiskAndAssessmentForm"))}
      isReview={isReview}
    >
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          paddingBottom: "0px",
          borderBottom: "none",
        }}
      >
        <FormProvider {...form}>
          <form
            className={style.addReferralForm}
            onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("RiskAndAssessmentForm"))}
          >
            <div className={style.itemWrapper}>
              <h3>Risk Factors</h3>

              <p className={style.riskGroupHeader}>Smoking</p>
              <div
                className={style.inputGroupGrid3Columns}
                style={{ marginTop: "12px" }}
              >
                <FormField name="smokingStatus">
                  <label className={style.addReferralFormLabel}>
                    Smoking Status
                  </label>
                  <Select
                    options={SMOKING_STATUS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("smokingStatus")}
                    onChange={(value) =>
                      form.setValue("smokingStatus", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                </FormField>
                <FormField name="vapingStatus">
                  <label className={style.addReferralFormLabel}>
                    Vaping Status
                  </label>
                  <Select
                    options={OCF_VAPING_STATUS_OPTIONS}
                    placeholder="Select"
                    disabled={isReview}
                    value={form.watch("vapingStatus")}
                    onChange={(value) =>
                      form.setValue("vapingStatus", value, {
                        shouldValidate: true,
                      })
                    }
                    required
                  />
                </FormField>
              </div>
            </div>
            <div
              className={style.itemWrapper}
              style={{
                borderBottom: "none",
                marginBottom: "8px",
                paddingBottom: "0px",
              }}
            >
              <h3>QRisk</h3>
              <div
                className={style.inputGroupGrid2Columns}
                style={{ marginTop: "16px" }}
              >
                <FormField name="qRisk3Score">
                  <label className={style.addReferralFormLabel}>
                    QRisk 3 Score
                  </label>
                  <input type="hidden" {...form.register("qRisk3Score")} />
                  <CalculateInputTrigger
                    value={form.watch("qRisk3Score")}
                    onClick={() => setIsQRisk3ModalOpen(true)}
                    disabled={isReview}
                  />
                </FormField>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
      <QRisk3CalculatorModal
        isOpen={isQRisk3ModalOpen}
        onClose={() => setIsQRisk3ModalOpen(false)}
        onConfirm={(score, ratio, ethrisk, conditions, cigarettesPerDay) => {
          form.setValue("qRisk3Score", score, { shouldValidate: true });
          form.setValue("tcHdlRatio", ratio);
          form.setValue("qRisk3Ethrisk", ethrisk);
          form.setValue("qRisk3Conditions", conditions);
          form.setValue("qRisk3CigarettesPerDay", cigarettesPerDay);
        }}
        // Only the QRisk3-only inputs round-trip here. Cholesterol, BP and BMI are
        // prefill-only — their source fields live on the now-unmounted Tests and
        // Review & Exam steps, so we deliberately do NOT write those back.
        onFieldChange={({ sbps5, ethrisk, cigarettesPerDay }) => {
          form.setValue("qRisk3Sbps5", sbps5 ?? "");
          form.setValue("qRisk3Ethrisk", ethrisk);
          form.setValue("qRisk3CigarettesPerDay", cigarettesPerDay ?? "");
        }}
        // Prefill-only from prior steps: bloods (Tests step) + obs (Review & Exam
        // step) + smoking status (this step); patient age/sex read in the modal.
        prefillBmi={data?.bmi}
        prefillSbp={data?.bp1}
        prefillSmokingStatus={form.watch("smokingStatus")}
        prefillTotalCholesterol={data?.totalCholesterol}
        prefillHdl={data?.hdlCholesterol}
        prefillSbps5={form.watch("qRisk3Sbps5")}
        prefillEthrisk={form.watch("qRisk3Ethrisk")}
        prefillConditions={form.watch("qRisk3Conditions")}
        prefillCigarettesPerDay={form.watch("qRisk3CigarettesPerDay")}
        // Seed the calculator from the ethnicity chosen on this review's initial
        // step (falls back to the patient record inside the modal).
        selectedEthnicityCode={data?.ethnicityCode}
      />
    </ItemFormWrapper>
  );
};
